import { IFilter, IEventoGenerico, IUbicacion } from 'modelos/src';

/**
 * Agrupamiento personalizado del listado vivo de eventos.
 *
 * Dos mecanismos distintos que conviene no mezclar:
 *  - FILTRO: qué eventos entran. Va al SERVIDOR (ver `construirFiltroMongo`).
 *  - CRITERIO: cómo se parten en cards los que entraron (`CriterioAgrupacion`).
 *
 * El filtro va al servidor y no al array en memoria porque el listado vivo pide
 * `limit = 1000` y en producción hay ~94.000 eventos pendientes, con 12 clientes
 * que ven más de 1000 (el más grande, 28.194). Filtrar la página cargada daría
 * un resultado calculado sobre una fracción arbitraria — los más nuevos, por el
 * `sort: '-fechaCreacion'` — sin que se note.
 * Ver PLAN_AGRUPAMIENTO_PERSONALIZADO_EVENTOS.md.
 */

/** Criterio de bucket. Uno solo a la vez, sin anidar. */
export type CriterioAgrupacion =
  | 'zona'
  | 'cliente'
  | 'categoria'
  | 'tipoEntidad'
  | 'entidad'
  | 'prioridad';

export const CRITERIOS_AGRUPACION: {
  valor: CriterioAgrupacion;
  label: string;
}[] = [
  { valor: 'zona', label: 'Zona geográfica' },
  { valor: 'cliente', label: 'Cliente' },
  { valor: 'categoria', label: 'Categoría' },
  { valor: 'tipoEntidad', label: 'Módulo' },
  { valor: 'entidad', label: 'Dispositivo' },
  { valor: 'prioridad', label: 'Prioridad' },
];

export interface IFiltroAgrupamientoPersonalizado {
  categorias?: string[];
  tiposEvento?: string[];
  idsClientes?: string[];
  /** Cliente en cuya jerarquía tiene que estar el evento */
  idsAncestros?: string[];
  idsEntidades?: string[];
  estados?: string[];
  prioridadDesde?: number;
  prioridadHasta?: number;
  /** ISO. Antigüedad del evento por `fechaCreacion` */
  fechaDesde?: string;
  fechaHasta?: string;
  repetidoDesde?: number;
  /** true = sólo los que nadie está atendiendo */
  soloSinAtender?: boolean;
  idsUsuariosAtendiendo?: string[];
  /** Zonas geográficas (IUbicacion con polígono) */
  idsZonas?: string[];
}

export const CLAVE_FILTRO_PERSONALIZADO = 'filtroAgrupamientoPersonalizado';
export const CLAVE_CRITERIO_PERSONALIZADO = 'criterioAgrupamientoPersonalizado';

export function filtroVacio(): IFiltroAgrupamientoPersonalizado {
  return {};
}

/** true si el filtro no restringe nada (una sola card con todo adentro) */
export function filtroEstaVacio(f?: IFiltroAgrupamientoPersonalizado): boolean {
  if (!f) return true;
  return !Object.values(f).some((v) =>
    Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '',
  );
}

// ── Persistencia ────────────────────────────────────────────────────────────
// localStorage, al lado del `modoAgrupacionGenericos` que ya usa el listado.
// Decisión explícita: el filtro NO es una entidad, no se comparte ni se nombra.

export function leerFiltroGuardado(): IFiltroAgrupamientoPersonalizado {
  try {
    const raw = localStorage.getItem(CLAVE_FILTRO_PERSONALIZADO);
    if (!raw) return filtroVacio();
    const parsed = JSON.parse(raw);
    // Un localStorage de otra versión del filtro no debe romper el listado
    return parsed && typeof parsed === 'object' ? parsed : filtroVacio();
  } catch {
    return filtroVacio();
  }
}

export function guardarFiltro(f: IFiltroAgrupamientoPersonalizado): void {
  try {
    localStorage.setItem(CLAVE_FILTRO_PERSONALIZADO, JSON.stringify(f ?? {}));
  } catch (error) {
    console.error('No se pudo guardar el filtro personalizado', error);
  }
}

export function leerCriterioGuardado(): CriterioAgrupacion {
  const raw = localStorage.getItem(CLAVE_CRITERIO_PERSONALIZADO);
  const valido = CRITERIOS_AGRUPACION.some((c) => c.valor === raw);
  return valido ? (raw as CriterioAgrupacion) : 'cliente';
}

export function guardarCriterio(criterio: CriterioAgrupacion): void {
  try {
    localStorage.setItem(CLAVE_CRITERIO_PERSONALIZADO, criterio);
  } catch (error) {
    console.error('No se pudo guardar el criterio', error);
  }
}

// ── Filtro Mongo ────────────────────────────────────────────────────────────

/**
 * Qué tipos de evento admite cada categoría de zona.
 *
 * Las zonas NO son globales: cada `categoria` de IUbicacion pertenece a un
 * módulo y se dibuja desde su solapa (alarmas/zonas/Alarmas,
 * luminarias/zonas/Luminarias, vehiculos/ubicaciones/Vehiculos, etc.). Una zona
 * de alarmas no dice nada sobre un gateway que cae adentro del polígono, así que
 * el filtro y el bucket tienen que acotarse por acá.
 *
 * Los tipos que NO figuran en ningún valor de este mapa (Tracker, Gateway,
 * BotonBLE, Sirena) no tienen categoría de zona propia: no se pueden filtrar ni
 * agrupar por zona aunque su evento traiga punto. Si hiciera falta, primero hay
 * que crear la categoría de ubicación de ese módulo.
 */
export const TIPOS_EVENTO_POR_CATEGORIA_ZONA: Record<string, string[]> = {
  Alarmas: ['Evento Alarma'],
  Luminarias: ['Evento Luminaria'],
  Vehiculos: ['Evento Vehiculo'],
  Activos: ['Evento Activo'],
  Terminal: ['Evento Colectivo'],
};

/** Tipos de evento que una zona de esta categoría puede llegar a matchear */
export function tiposEventoDeZona(categoria?: string): string[] {
  return TIPOS_EVENTO_POR_CATEGORIA_ZONA[categoria ?? ''] ?? [];
}

/** true si la zona puede aplicarle a ese tipo de evento */
export function zonaAplicaATipoEvento(
  categoriaZona: string | undefined,
  tipoEvento: string | undefined,
): boolean {
  if (!tipoEvento) return false;
  return tiposEventoDeZona(categoriaZona).includes(tipoEvento);
}

/** Tipos de evento cuyo `valores.geojson` ES el punto donde ocurrió el evento */
export const TIPOS_EVENTO_CON_PUNTO_PROPIO = [
  'Evento Vehiculo',
  'Evento Tracker',
  'Evento Colectivo',
  'Evento Luminaria',
  'Evento Gateway',
  'Evento BotonBLE',
  'Evento Sirena',
];

/**
 * Traduce el filtro de la UI a cláusulas Mongo, para mezclarlas con el filtro
 * base del listado vivo (`requiereAtencion` + `filtrador` + horarios), que NO
 * se toca.
 *
 * `idsEntidadesEnZona` viene resuelto aparte: son las entidades fijas (alarmas,
 * activos) cuyo domicilio cae dentro de las zonas elegidas. Las móviles se
 * resuelven contra el punto del evento y no necesitan esa lista — ver
 * `construirClausulaZona`.
 */
export function construirFiltroMongo(
  f: IFiltroAgrupamientoPersonalizado,
  zonas?: IUbicacion[],
  idsEntidadesEnZona?: string[],
): IFilter<IEventoGenerico> {
  const filtro: Record<string, unknown> = {};
  const and: Record<string, unknown>[] = [];

  if (f.categorias?.length) filtro['categoria'] = { $in: f.categorias };
  if (f.tiposEvento?.length) filtro['tipoEvento'] = { $in: f.tiposEvento };
  if (f.idsClientes?.length) filtro['idCliente'] = { $in: f.idsClientes };
  if (f.idsAncestros?.length) filtro['idsAncestros'] = { $in: f.idsAncestros };
  if (f.estados?.length) filtro['estado'] = { $in: f.estados };

  if (f.prioridadDesde != null || f.prioridadHasta != null) {
    const rango: Record<string, number> = {};
    if (f.prioridadDesde != null) rango['$gte'] = f.prioridadDesde;
    if (f.prioridadHasta != null) rango['$lte'] = f.prioridadHasta;
    filtro['prioridad'] = rango;
  }

  if (f.fechaDesde || f.fechaHasta) {
    const rango: Record<string, string> = {};
    if (f.fechaDesde) rango['$gte'] = f.fechaDesde;
    if (f.fechaHasta) rango['$lte'] = f.fechaHasta;
    filtro['fechaCreacion'] = rango;
  }

  if (f.repetidoDesde != null) {
    filtro['repetido'] = { $gte: f.repetidoDesde };
  }

  // `soloSinAtender` y `idsUsuariosAtendiendo` son excluyentes: si se piden los
  // dos, gana el explícito (pedir "de Juan" y "sin atender" no tiene resultado).
  if (f.idsUsuariosAtendiendo?.length) {
    filtro['idsUsuariosAtendiendo'] = { $in: f.idsUsuariosAtendiendo };
  } else if (f.soloSinAtender) {
    filtro['idsUsuariosAtendiendo'] = { $size: 0 };
  }

  // `idsEntidades` puede chocar con el `idEntidad` que arma la zona: los dos
  // van al $and para que se intersequen en vez de sobrescribirse.
  if (f.idsEntidades?.length) {
    and.push({ idEntidad: { $in: f.idsEntidades } });
  }

  const clausulaZona = construirClausulaZona(f, zonas, idsEntidadesEnZona);
  if (clausulaZona) and.push(clausulaZona);

  if (and.length) filtro['$and'] = and;
  return filtro as IFilter<IEventoGenerico>;
}

/**
 * Zona geográfica. Dos ramas en un `$or`, y las dos hacen falta:
 *
 *  - Entidades MÓVILES: contra el punto del evento (`valores.geojson`), que ya
 *    viaja en el `select` del listado. NO contra la posición actual de la
 *    entidad: el vehículo se movió, y la zona del evento es la del punto
 *    histórico. Es correctitud, no optimización.
 *  - Entidades FIJAS sin punto en el evento (alarma 0%, activo 0% medido en
 *    prod): por `idEntidad`, con la lista que resolvió el llamador con un
 *    `$geoWithin` sobre `ubicacions`.
 */
export function construirClausulaZona(
  f: IFiltroAgrupamientoPersonalizado,
  zonas?: IUbicacion[],
  idsEntidadesEnZona?: string[],
): Record<string, unknown> | null {
  if (!f.idsZonas?.length) return null;

  const ramas: Record<string, unknown>[] = [];

  const elegidas = (zonas ?? []).filter(
    (z) =>
      z._id &&
      f.idsZonas.includes(z._id) &&
      (z.geojson?.type === 'Polygon' || z.geojson?.type === 'MultiPolygon'),
  );

  for (const zona of elegidas) {
    const tipos = tiposEventoDeZona(zona.categoria);
    if (!tipos.length) continue;
    // Las entidades fijas van por la rama de idEntidad, no por geometria: su
    // evento no trae punto (alarma 0% medido en prod).
    const tiposConPunto = tipos.filter((t) =>
      TIPOS_EVENTO_CON_PUNTO_PROPIO.includes(t),
    );
    if (!tiposConPunto.length) continue;
    ramas.push({
      $and: [
        { tipoEvento: { $in: tiposConPunto } },
        { 'valores.geojson': { $geoWithin: { $geometry: zona.geojson } } },
      ],
    });
  }

  if (idsEntidadesEnZona?.length) {
    ramas.push({ idEntidad: { $in: idsEntidadesEnZona } });
  }

  if (!ramas.length) {
    // Zonas pedidas pero ninguna resoluble: que no matchee nada es más honesto
    // que ignorar el filtro y devolver todo.
    return { _id: { $in: [] } };
  }
  return ramas.length === 1 ? ramas[0] : { $or: ramas };
}
